const ACCESS_TOKEN_KEY = 'ecom_admin_access_token';
const REFRESH_TOKEN_KEY = 'ecom_admin_refresh_token';
const TOKEN_EXPIRY_KEY = 'ecom_admin_token_expiry';
const CODE_VERIFIER_KEY = 'ecom_admin_code_verifier';
const STATE_KEY = 'ecom_admin_oauth_state';
const ID_TOKEN_KEY = 'ecom_admin_id_token';

export interface TokenData {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresIn?: number;
}

export interface UserInfo {
  sub?: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  role?: string;
  roles?: string[];
  [key: string]: unknown;
}

class AuthStore {
  private accessToken: string | null = null;

  constructor() {
    this.accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  setTokens(data: TokenData): void {
    this.accessToken = data.accessToken;
    sessionStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);

    if (data.refreshToken) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    }

    if (data.idToken) {
      sessionStorage.setItem(ID_TOKEN_KEY, data.idToken);
    }

    if (data.expiresIn) {
      const expiry = Date.now() + data.expiresIn * 1000;
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
    }
  }

  getAccessToken(): string | null {
    if (this.isTokenExpired()) {
      this.clearTokens();
      return null;
    }
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  }

  getIdToken(): string | null {
    return sessionStorage.getItem(ID_TOKEN_KEY);
  }

  isTokenExpired(): boolean {
    const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) return false;
    return Date.now() > parseInt(expiry, 10);
  }

  isAuthenticated(): boolean {
    return this.getAccessToken() !== null;
  }

  getUserInfo(): UserInfo | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded) as UserInfo;
    } catch {
      return null;
    }
  }

  clearTokens(): void {
    this.accessToken = null;
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ID_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  setCodeVerifier(verifier: string): void {
    sessionStorage.setItem(CODE_VERIFIER_KEY, verifier);
  }

  getCodeVerifier(): string | null {
    return sessionStorage.getItem(CODE_VERIFIER_KEY);
  }

  clearCodeVerifier(): void {
    sessionStorage.removeItem(CODE_VERIFIER_KEY);
  }

  setState(state: string): void {
    sessionStorage.setItem(STATE_KEY, state);
  }

  getState(): string | null {
    return sessionStorage.getItem(STATE_KEY);
  }

  clearState(): void {
    sessionStorage.removeItem(STATE_KEY);
  }
}

export const authStore = new AuthStore();
